import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../utils/supabase';
import { Modal, ModalContent, ModalCloseButton } from '../common';
import { previewPayment, applyPayment } from '../../services/paymentLedgerService';
import {
  fetchPaymentSearchAliasesByClub,
  mergeAliasesIntoPlayers,
  playerMatchesPaymentSearch,
} from '../../services/clubMemberPaymentAliasService';

/**
 * RecordPaymentModal
 * 
 * Modal component for recording payments.
 * Features:
 * - Payer selection (who is making the payment)
 * - Optional "Also paying for" (e.g. guests) so one payment can cover multiple participants
 * - Payment amount, currency, method, note
 * - Preview of which sessions (and participants) will be paid
 */
const RecordPaymentModal = ({ 
  isOpen, 
  onClose, 
  clubId, 
  onPaymentRecorded,
  preselectedPlayer = null,
  defaultCurrency = 'GBP',
  prioritizeSessionId = null
}) => {
  const [players, setPlayers] = useState([]);
  const [selectedPayer, setSelectedPayer] = useState(preselectedPlayer);
  const [payingForOthers, setPayingForOthers] = useState(false); // Toggle: "paying for others as well"
  const [payForPlayerIds, setPayForPlayerIds] = useState([]); // Also paying for (e.g. guests), shown only when payingForOthers is true
  const [otherParticipantsSearch, setOtherParticipantsSearch] = useState(''); // Filter for "also paying for" list
  const [paymentAmount, setPaymentAmount] = useState('');
  const [currencyCode, setCurrencyCode] = useState(defaultCurrency);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [payerSearch, setPayerSearch] = useState('');

  // Get current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: player } = await supabase
          .from('players')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        
        if (player) {
          setCurrentUser(player);
        }
      }
    };

    if (isOpen) {
      fetchCurrentUser();
    }
  }, [isOpen]);

  // Fetch players in the club
  useEffect(() => {
    const fetchPlayers = async () => {
      if (!clubId || !isOpen) return;

      const { data, error } = await supabase
        .from('players_club')
        .select(`
          player:players(
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('club_id', clubId)
        .order('player(first_name)');

      if (error) {
        console.error('Error fetching players:', error);
        setError('Failed to load players');
        return;
      }

      const rawList = data
        .map(pc => pc.player)
        .filter(p => p);

      const aliasRes = await fetchPaymentSearchAliasesByClub(clubId);
      const playersList = mergeAliasesIntoPlayers(rawList, aliasRes.map || {});

      setPlayers(playersList);
    };

    fetchPlayers();
  }, [clubId, isOpen]);

  useEffect(() => {
    if (isOpen) setPayerSearch('');
  }, [isOpen, clubId]);

  // Set preselected player when provided (as payer)
  useEffect(() => {
    if (preselectedPlayer) {
      setSelectedPayer(preselectedPlayer);
    }
  }, [preselectedPlayer]);

  // Clear "also paying for" if payer changes and was in the list
  useEffect(() => {
    if (selectedPayer?.id) {
      setPayForPlayerIds(prev => prev.filter(id => id !== selectedPayer.id));
    }
  }, [selectedPayer?.id]);

  // When user turns off "paying for others", clear the selection and search
  useEffect(() => {
    if (!payingForOthers) {
      setPayForPlayerIds([]);
      setOtherParticipantsSearch('');
    }
  }, [payingForOthers]);

  // Other participants list: exclude payer, filter by name / email / bank hints
  const otherParticipantsList = useMemo(() => {
    const excludedPayer = players.filter(p => p.id !== selectedPayer?.id);
    if (!otherParticipantsSearch.trim()) return excludedPayer;
    return excludedPayer.filter((p) => playerMatchesPaymentSearch(p, otherParticipantsSearch.trim()));
  }, [players, selectedPayer?.id, otherParticipantsSearch]);

  const filteredPayerOptions = useMemo(() => {
    if (!payerSearch.trim()) return players;
    return players.filter((p) => playerMatchesPaymentSearch(p, payerSearch.trim()));
  }, [players, payerSearch]);

  // Load preview when inputs change
  const loadPreview = useCallback(async () => {
    if (!selectedPayer || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      setPreview(null);
      return;
    }

    setIsLoadingPreview(true);
    setError(null);

    const result = await previewPayment(
      selectedPayer.id,
      clubId,
      parseFloat(paymentAmount),
      currencyCode,
      {
        payForPlayerIds: payForPlayerIds.length ? payForPlayerIds : undefined,
        prioritizeSessionId: prioritizeSessionId || undefined
      }
    );

    if (result.success) {
      setPreview(result);
    } else {
      setError(result.error);
      setPreview(null);
    }

    setIsLoadingPreview(false);
  }, [selectedPayer, payForPlayerIds, paymentAmount, clubId, currencyCode, prioritizeSessionId]);

  // When paying for others: each selected other must get at least one full session in the preview
  const uncoveredOtherIds =
    preview?.sessionsToBePaid && payForPlayerIds.length > 0
      ? payForPlayerIds.filter(
          (id) => !preview.sessionsToBePaid.some((s) => s.participantPlayerId === id)
        )
      : [];
  const uncoveredNames = uncoveredOtherIds
    .map((id) => players.find((p) => p.id === id))
    .filter(Boolean)
    .map((p) => `${p.first_name} ${p.last_name}`.trim())
    .join(', ');
  const payingForOthersIncomplete = uncoveredOtherIds.length > 0;

  // Auto-load preview when inputs change
  useEffect(() => {
    const timer = setTimeout(() => {
      loadPreview();
    }, 500);

    return () => clearTimeout(timer);
  }, [loadPreview]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPayer || !paymentAmount || !currentUser) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const result = await applyPayment(
      selectedPayer.id,
      clubId,
      parseFloat(paymentAmount),
      currentUser.id,
      {
        payForPlayerIds: payForPlayerIds.length ? payForPlayerIds : undefined,
        prioritizeSessionId: prioritizeSessionId || undefined,
        note: note.trim() || undefined,
        paymentMethod,
        currencyCode
      }
    );

    setIsSubmitting(false);

    if (result.success) {
      // Reset form
      setSelectedPayer(preselectedPlayer);
      setPayingForOthers(false);
      setPayForPlayerIds([]);
      setPaymentAmount('');
      setNote('');
      setPreview(null);
      
      // Notify parent
      if (onPaymentRecorded) {
        onPaymentRecorded(result);
      }
      
      // Close modal
      onClose();
    } else {
      setError(result.error || 'Failed to record payment');
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedPayer(preselectedPlayer);
      setPayingForOthers(false);
      setPayForPlayerIds([]);
      setPaymentAmount('');
      setNote('');
      setPreview(null);
      setError(null);
      onClose();
    }
  };

  const togglePayFor = (playerId) => {
    setPayForPlayerIds(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  if (!isOpen) return null;

  const formatCurrency = (amount) => {
    const symbols = { GBP: '£', USD: '$', EUR: '€', CAD: 'C$', AUD: 'A$' };
    return `${symbols[currencyCode] || currencyCode} ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnBackdropClick={!isSubmitting}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="sticky top-0 z-10 shrink-0 border-b border-border-medium bg-surface px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">Record Payment</h2>
          <ModalCloseButton onClose={handleClose} disabled={isSubmitting} />
        </div>

        <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-danger-light border border-danger-dark/30 text-danger-dark px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Payer Selection */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Payer *
            </label>
            {preselectedPlayer == null && (
              <input
                type="text"
                value={payerSearch}
                onChange={(e) => setPayerSearch(e.target.value)}
                placeholder="Filter by name, email, or saved bank hint…"
                disabled={isSubmitting}
                className="w-full mb-2 px-3 py-2 text-sm border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface placeholder:text-text-muted"
              />
            )}
            <select
              value={selectedPayer?.id || ''}
              onChange={(e) => {
                const player = players.find(p => p.id === e.target.value);
                setSelectedPayer(player);
              }}
              disabled={preselectedPlayer !== null || isSubmitting}
              className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface disabled:bg-surface-subtle"
              required
            >
              <option value="">Select payer...</option>
              {filteredPayerOptions.map(player => (
                <option key={player.id} value={player.id}>
                  {player.first_name} {player.last_name}
                  {player.payment_search_aliases?.length
                    ? ` — ${player.payment_search_aliases.slice(0, 2).join(', ')}${player.payment_search_aliases.length > 2 ? '…' : ''}`
                    : ''}
                </option>
              ))}
            </select>
            {preselectedPlayer == null && payerSearch.trim() && filteredPayerOptions.length === 0 && (
              <p className="text-xs text-text-muted mt-1">No members match this filter.</p>
            )}
          </div>

          {/* Paying for others? (toggle first, then list) */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={payingForOthers}
                onChange={(e) => setPayingForOthers(e.target.checked)}
                disabled={!selectedPayer || isSubmitting}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm font-medium text-text-secondary">
                This payment also covers other participants (e.g. guests)
              </span>
            </label>
            {payingForOthers && (
              <div className="mt-3 pl-6">
                <p className="text-xs text-text-muted mb-2">
                  Select who else this payment will cover. Each person selected must receive at least one full session payment.
                </p>
                <input
                  type="text"
                  value={otherParticipantsSearch}
                  onChange={(e) => setOtherParticipantsSearch(e.target.value)}
                  placeholder="Search participants..."
                  disabled={!selectedPayer || isSubmitting}
                  className="w-full mb-2 px-3 py-2 text-sm border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface placeholder:text-text-muted"
                />
                <div className="max-h-32 overflow-y-auto border border-border-medium rounded-md p-2 bg-surface-subtle space-y-1">
                  {otherParticipantsList.map(player => (
                    <label key={player.id} className="flex items-center gap-2 cursor-pointer hover:bg-surface-elevated rounded px-2 py-1">
                      <input
                        type="checkbox"
                        checked={payForPlayerIds.includes(player.id)}
                        onChange={() => togglePayFor(player.id)}
                        disabled={!selectedPayer || isSubmitting}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm">
                        {player.first_name} {player.last_name}
                      </span>
                    </label>
                  ))}
                  {otherParticipantsList.length === 0 && (
                    <p className="text-sm text-text-muted italic">
                      {otherParticipantsSearch.trim() ? 'No participants match your search' : 'No other players in club'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Validation: when paying for others, each selected must get at least one full session */}
          {payingForOthersIncomplete && (
            <div className="bg-warning-light border border-warning text-warning-dark px-4 py-3 rounded text-sm">
              This amount does not cover a full session for: <strong>{uncoveredNames}</strong>. Please increase the payment amount or remove them from &quot;Also paying for&quot;.
            </div>
          )}

          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Payment Amount *
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              placeholder="0.00"
              required
            />
          </div>

          {/* Currency and Payment Method (side by side) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Currency
              </label>
              <select
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              >
                <option value="GBP">GBP (£)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="CAD">CAD (C$)</option>
                <option value="AUD">AUD (A$)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Note (Optional) */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              rows={2}
              placeholder="Add any notes about this payment..."
            />
          </div>

          {/* Preview Section */}
          {isLoadingPreview && (
            <div className="bg-surface-subtle border border-border-medium rounded-lg p-4">
              <p className="text-text-secondary text-center">Loading preview...</p>
            </div>
          )}

          {preview && !isLoadingPreview && (
            <div className="bg-info-light dark:bg-primary/10 border border-border-medium rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-text-primary flex items-center gap-2">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Payment Preview
              </h3>

              {preview.currentCredit > 0 && (
                <div className="text-sm text-text-secondary bg-surface rounded p-2">
                  <strong>Current Credit:</strong> {formatCurrency(preview.currentCredit)}
                </div>
              )}

              {preview.sessionsToBePaid.length > 0 ? (
                <>
                  <p className="text-sm text-text-secondary">
                    This payment will mark the following sessions as paid:
                  </p>
                  <div className="space-y-2">
                    {preview.sessionsToBePaid.map((session, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm bg-surface rounded p-2">
                        <svg className="w-5 h-5 text-success-dark flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="flex-grow">
                          <strong>{session.name}</strong> - {formatDate(session.date)}
                          {session.participantName && (
                            <span className="text-text-muted"> ({session.participantName})</span>
                          )}
                        </span>
                        <span className="font-medium">{formatCurrency(session.price)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-sm text-text-secondary bg-surface rounded p-2">
                    <strong>Total Allocated:</strong> {formatCurrency(preview.amountToBeAllocated)}
                  </div>
                </>
              ) : (
                <p className="text-sm text-text-secondary">
                  No sessions will be paid. The full amount will be added to credit.
                </p>
              )}

              <div className="text-sm font-semibold text-text-primary bg-surface rounded p-2">
                Remaining Credit: {formatCurrency(preview.remainingCredit)}
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-surface-subtle border-t border-border-medium px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-text-secondary hover:bg-surface-elevated rounded-md transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedPayer || !paymentAmount || parseFloat(paymentAmount) <= 0 || payingForOthersIncomplete}
            className="px-4 py-2 bg-primary text-text-inverse rounded-md hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Recording...
              </>
            ) : (
              'Confirm Payment'
            )}
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default RecordPaymentModal;
